import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Plus, TrendingUp, TrendingDown, Search, X, Loader2, CheckCircle2,
  AlertTriangle, Clock, Bell,
  Trash2, List, Activity, CheckCircle, ListTodo
} from "lucide-react";
import TareasPanel from "../components/TareasPanel";   // Fix DO (2026-09-22)
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { formatPYGCompact, formatDate, formatPYG } from "../utils/format";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import {
  ComposedChart, Bar, Line, ReferenceLine, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from "recharts";

// ============ CONSTANTS ============
const CATEGORIES_GASTO = [
  "Materiales Eléctricos", "Mano de Obra", "Transporte", "Equipos",
  "Herramientas", "Iluminación", "Servicios", "Administrativo", "Otros"
];
const CATEGORIES_INGRESO = ["Certificación", "Anticipo", "Liquidación", "Pago por Avance", "Otros"];

// Fix P (2026-08-31): los seis rubros de imputación (finance_utils.py), con
// etiqueta legible en español para el desplegable del formulario de gasto.
const IMPUTACION_LABELS = {
  materiales: "Materiales",
  mano_obra: "Mano de Obra",
  gastos_admin: "Gastos Administrativos",
  equipamientos: "Equipamientos",
  menores_imprevistos: "Menores / Imprevistos",
  caja_chica: "Caja Chica",
};
const IMPUTACION_OPTIONS = Object.keys(IMPUTACION_LABELS);

const PAYMENT_BADGE = {
  pendiente: "bg-amber-100 text-amber-700",
  pagado: "bg-emerald-100 text-emerald-700",
  parcial: "bg-blue-100 text-blue-700",
};

const PAYMENT_LABEL = { pendiente: "Pendiente", pagado: "Pagado", parcial: "Parcial" };

const AGING_CONFIG = [
  { key: "bucket_0_30", label: "0 – 30 días", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  { key: "bucket_31_60", label: "31 – 60 días", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  { key: "bucket_61_90", label: "61 – 90 días", color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200" },
  { key: "bucket_91_plus", label: "+90 días", color: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
];

const TABS = [
  { id: "tareas", label: "Tareas", icon: ListTodo },   // Fix DO (2026-09-22)
  { id: "flujo", label: "Flujo de Caja", icon: Activity },   // Fix IS: reemplaza a «Resumen»
  { id: "transacciones", label: "Transacciones", icon: List },
  { id: "aging", label: "CxC / CxP", icon: Clock },
  { id: "alertas", label: "Alertas", icon: Bell },
];

// ============ TRANSACTION MODAL ============
function TransactionModal({ open, onClose, onSaved, projects }) {
  const emptyForm = {
    project_id: "", type: "gasto", category: "", amount_pyg: "",
    description: "", date: new Date().toISOString().split("T")[0],
    has_iva: true, supplier: "", invoice_number: "", payment_status: "pendiente", due_date: "",
    imputacion: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [sugerencia, setSugerencia] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const categories = form.type === "gasto" ? CATEGORIES_GASTO : CATEGORIES_INGRESO;
  const iva = form.amount_pyg && form.has_iva ? Number(form.amount_pyg) * 0.10 : 0;

  // Fix P (2026-08-31): la inferencia por palabras clave se sigue ofreciendo
  // como sugerencia (no se aplica sola) cuando cambia categoría o descripción.
  useEffect(() => {
    if (form.type !== "gasto" || (!form.category && !form.description)) { setSugerencia(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/finance/sugerir-imputacion`, {
        params: { categoria: form.category, descripcion: form.description },
        withCredentials: true,
      }).then(({ data }) => setSugerencia(data)).catch(() => setSugerencia(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.category, form.description, form.type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id || !form.category || !form.amount_pyg || !form.description || !form.date) {
      setError("Complete todos los campos obligatorios");
      return;
    }
    if (form.type === "gasto" && !form.imputacion) {
      setError("Seleccione a qué rubro se imputa el gasto");
      return;
    }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/finance/transactions`, {
        ...form,
        amount_pyg: Number(form.amount_pyg),
        supplier: form.supplier || null,
        invoice_number: form.invoice_number || null,
        due_date: form.due_date || null,
        imputacion: form.type === "gasto" ? form.imputacion : null,
      }, { withCredentials: true });
      toast.success("Transacción registrada exitosamente");
      onSaved();
      onClose();
      setForm(emptyForm);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al registrar transacción");
    } finally { setSaving(false); }

  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="transaction-modal">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Nueva Transacción</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors"><X size={18} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {["gasto", "ingreso"].map((t) => (
              <button key={t} type="button" onClick={() => setForm(f => ({ ...f, type: t, category: "" }))}
                className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${form.type === t ? t === "gasto" ? "border-red-400 bg-red-50 text-red-700" : "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"}`}
                data-testid={`type-${t}-btn`}>
                <span className="flex items-center justify-center gap-1.5">
                  {t === "gasto" ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
                  {t === "gasto" ? "Gasto" : "Ingreso"}
                </span>
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Proyecto *</label>
            <select name="project_id" value={form.project_id} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white" data-testid="txn-project-select">
              <option value="">Seleccionar proyecto...</option>
              {projects.map(p => <option key={p._id} value={p._id}>{`${p.code} · ${p.client || "—"} · ${p.name}`}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Categoría *</label>
            <select name="category" value={form.category} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white" data-testid="txn-category-select">
              <option value="">Seleccionar categoría...</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Descripción *</label>
            <input name="description" value={form.description} onChange={handleChange} placeholder="Detalle de la transacción" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="txn-description-input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Monto (PYG) *</label>
              <input name="amount_pyg" type="number" value={form.amount_pyg} onChange={handleChange} placeholder="0" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="txn-amount-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha *</label>
              <input name="date" type="date" value={form.date} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="txn-date-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Proveedor</label>
              <input name="supplier" value={form.supplier} onChange={handleChange} placeholder="Nombre del proveedor" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="txn-supplier-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">N° Factura</label>
              <input name="invoice_number" value={form.invoice_number} onChange={handleChange} placeholder="001-001-0000001" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] font-mono" data-testid="txn-invoice-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Estado de Pago</label>
              <select name="payment_status" value={form.payment_status} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white" data-testid="txn-status-select">
                <option value="pendiente">Pendiente</option>
                <option value="pagado">Pagado</option>
                <option value="parcial">Parcial</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Vencimiento</label>
              <input name="due_date" type="date" value={form.due_date} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="txn-due-date-input" />
            </div>
          </div>

          {form.type === "gasto" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Imputación *</label>
              <select name="imputacion" value={form.imputacion} onChange={handleChange}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white"
                data-testid="txn-imputacion-select">
                <option value="">Seleccionar rubro...</option>
                {IMPUTACION_OPTIONS.map(v => <option key={v} value={v}>{IMPUTACION_LABELS[v]}</option>)}
              </select>
              {sugerencia && (
                <div className={`mt-1.5 text-xs flex items-center gap-1.5 ${sugerencia.es_fallback ? "text-amber-600" : "text-gray-500"}`} data-testid="txn-imputacion-sugerencia">
                  {sugerencia.es_fallback ? <AlertTriangle size={12} /> : null}
                  {sugerencia.es_fallback
                    ? "El sistema no pudo deducir el rubro automáticamente — elegilo a mano."
                    : <>Sugerencia: <button type="button" onClick={() => setForm(f => ({ ...f, imputacion: sugerencia.sugerida }))} className="font-semibold text-[#2E6DA4] hover:underline" data-testid="txn-imputacion-sugerencia-btn">{IMPUTACION_LABELS[sugerencia.sugerida]}</button></>}
                </div>
              )}
            </div>
          )}

          {form.type === "gasto" && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="has_iva" checked={form.has_iva} onChange={handleChange} className="w-4 h-4 accent-[#2E6DA4]" data-testid="txn-iva-checkbox" />
                <span className="text-sm font-medium text-gray-700">Incluir IVA (10%)</span>
              </label>
              {form.amount_pyg && form.has_iva && (
                <div className="mt-2.5 grid grid-cols-3 gap-2 text-xs text-gray-600">
                  <div>Base: <span className="font-semibold">{formatPYGCompact(Number(form.amount_pyg))}</span></div>
                  <div>IVA 10%: <span className="font-semibold text-amber-600">{formatPYGCompact(iva)}</span></div>
                  <div>Total: <span className="font-semibold">{formatPYGCompact(Number(form.amount_pyg) * 1.10)}</span></div>
                </div>
              )}
            </div>
          )}

          {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm" data-testid="txn-error"><AlertTriangle size={14} />{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium transition-all">Cancelar</button>
            <button type="submit" disabled={saving}
              className={`flex-1 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60 ${form.type === "gasto" ? "bg-red-500 hover:bg-red-600" : "bg-emerald-600 hover:bg-emerald-700"}`}
              data-testid="txn-save-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              {saving ? "Guardando..." : "Registrar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ============ TRANSACCIONES TAB ============
function TransaccionesTab({ transactions, projects, loading, onDelete, txPage, txTotalPages, txTotal, TX_LIMIT, onPageChange }) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("todos");
  const [projectFilter, setProjectFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("todos");

  const projectMap = Object.fromEntries(projects.map(p => [p._id, p]));

  const isOverdue = (t) => {
    if ((t.payment_status || "pendiente") === "pagado") return false;
    const checkDate = t.due_date || null;
    if (!checkDate) return false;
    return new Date(checkDate) < new Date();
  };

  const filtered = transactions.filter(t => {
    const matchType = typeFilter === "todos" || t.type === typeFilter;
    const matchProject = projectFilter === "todos" || t.project_id === projectFilter;
    const matchStatus = statusFilter === "todos" || (t.payment_status || "pendiente") === statusFilter;
    const q = search.toLowerCase();
    const matchSearch = !search ||
      t.description?.toLowerCase().includes(q) ||
      t.category?.toLowerCase().includes(q) ||
      (t.supplier || "").toLowerCase().includes(q) ||
      (t.invoice_number || "").toLowerCase().includes(q);
    return matchType && matchProject && matchStatus && matchSearch;
  });

  const handleDelete = async (txnId) => {
    if (!window.confirm("¿Eliminar esta transacción? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/finance/transactions/${txnId}`, { withCredentials: true });
      toast.success("Transacción eliminada");
      onDelete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar descripción, categoría, proveedor..." className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white" data-testid="finance-search-input" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["todos", "ingreso", "gasto"].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)} className={`px-3 py-2 rounded-xl text-xs font-semibold capitalize transition-all border ${typeFilter === t ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-300 hover:border-[#2E6DA4]/40"}`} data-testid={`finance-filter-${t}`}>{t}</button>
          ))}
        </div>
        <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white" data-testid="finance-project-filter">
          <option value="todos">Todos los proyectos</option>
          {projects.map(p => <option key={p._id} value={p._id}>{p.code}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white" data-testid="finance-status-filter">
          <option value="todos">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="pagado">Pagado</option>
          <option value="parcial">Parcial</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="transactions-table">
        {loading ? (
          <div className="p-6 space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Proyecto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Categoría</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Descripción</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden xl:table-cell">Proveedor</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Monto</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">IVA</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Estado</th>
                  <th className="px-4 py-3 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(t => {
                  const overdue = isOverdue(t);
                  const status = t.payment_status || "pendiente";
                  return (
                    <tr key={t._id} className="hover:bg-gray-50/60 transition-colors" data-testid="finance-transaction-row">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-xs">{formatDate(t.date)}</td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="font-mono text-xs text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">{projectMap[t.project_id]?.code || "—"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${t.type === "ingreso" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                          {t.type === "ingreso" ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                          {t.type === "ingreso" ? "Ingreso" : "Gasto"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 hidden md:table-cell text-xs">{t.category}</td>
                      <td className="px-4 py-3 text-gray-500 hidden lg:table-cell text-xs max-w-[180px] truncate">{t.description}</td>
                      <td className="px-4 py-3 text-gray-500 hidden xl:table-cell text-xs max-w-[120px] truncate">{t.supplier || "—"}</td>
                      <td className={`px-4 py-3 text-right font-semibold tabular-nums whitespace-nowrap text-sm ${t.type === "ingreso" ? "text-emerald-700" : "text-red-600"}`}>
                        {t.type === "ingreso" ? "+" : "−"}{formatPYGCompact(t.amount_pyg)}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-amber-600 hidden md:table-cell tabular-nums">
                        {(t.iva_amount || 0) > 0 ? formatPYGCompact(t.iva_amount) : "—"}
                      </td>
                      <td className="px-4 py-3 text-center hidden sm:table-cell">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${overdue ? "bg-red-100 text-red-700" : PAYMENT_BADGE[status] || "bg-gray-100 text-gray-600"}`}>
                          {overdue ? "Vencido" : PAYMENT_LABEL[status] || status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleDelete(t._id)} className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" data-testid="delete-transaction-btn" title="Eliminar">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="flex flex-col items-center justify-center py-14 text-gray-400">
                <List size={32} className="mb-2 text-gray-300" />
                <p className="text-sm">Sin transacciones que coincidan</p>
              </div>
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-gray-400 text-right">{filtered.length} de {transactions.length} en esta página · {txTotal} total</p>
      {txTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-1" data-testid="pagination-controls">
          <button
            disabled={txPage === 0}
            onClick={() => onPageChange(txPage - 1)}
            className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            data-testid="pagination-prev"
          >
            ← Anterior
          </button>
          <span className="text-xs text-gray-500 font-medium">
            Página {txPage + 1} de {txTotalPages}
          </span>
          <button
            disabled={txPage >= txTotalPages - 1}
            onClick={() => onPageChange(txPage + 1)}
            className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            data-testid="pagination-next"
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
}

// ============ FLUJO DE CAJA TAB ============
// Fix IS (2026-11-02): "este resumen no, prefiero que esté el flujo de caja y las cuentas por cobrar y por pagar ...
// que pueda filtrarse por una obra individual o por varias ... barras positiva y negativa para los ingresos y egresos
// y la línea que indique el estado monetario ... por mes, por semana o por día" (reunión CEN, 30/10). Reemplaza a
// «Resumen». Los datos salen de los cobros y de las Órdenes de Pago pagadas (GET /finance/flujo-real).
const AGRUPAR_FLUJO = [["dia", "Día"], ["semana", "Semana"], ["mes", "Mes"]];
function FlujoCajaTab({ projects }) {
  const [agrupar, setAgrupar] = useState("mes");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [obras, setObras] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [buscar, setBuscar] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const obrasKey = obras.join(",");

  useEffect(() => {
    let vivo = true;
    setLoading(true); setError("");
    const params = new URLSearchParams({ agrupar });
    if (desde) params.set("desde", desde);
    if (hasta) params.set("hasta", hasta);
    if (obrasKey) params.set("obras", obrasKey);
    axios.get(`${API}/finance/flujo-real?${params}`, { withCredentials: true })
      .then(({ data }) => { if (vivo) setData(data); })
      .catch(err => { if (vivo) { setData(null); setError(err.response?.data?.detail || "Error cargando el flujo de caja"); } })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, [agrupar, desde, hasta, obrasKey]);

  const nombreObra = (p) => `${p.code ? p.code + " · " : ""}${p.name || ""}`;
  const listaObras = (projects || []).filter(p => !buscar.trim() || nombreObra(p).toLowerCase().includes(buscar.trim().toLowerCase()));
  const toggleObra = (id) => setObras(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const textoObras = !obras.length ? "Todas las obras"
    : obras.length === 1 ? nombreObra((projects || []).find(p => p._id === obras[0]) || { name: "1 obra" }) : `${obras.length} obras`;
  const serie = data?.serie || [];
  const chartData = serie.map(p => ({ etiqueta: p.etiqueta, Cobrado: p.cobrado, Pagado: -p.pagado, Saldo: p.saldo }));
  const kpis = data ? [
    { label: "Cobrado", value: formatPYGCompact(data.total_cobrado), color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", testId: "flujo-kpi-cobrado" },
    { label: "Pagado", value: formatPYGCompact(data.total_pagado), color: "text-red-700", bg: "bg-red-50", border: "border-red-200", testId: "flujo-kpi-pagado" },
    { label: "Saldo al cierre", value: `${data.saldo_final < 0 ? "−" : ""}${formatPYGCompact(Math.abs(data.saldo_final))}`, color: data.saldo_final < 0 ? "text-red-700" : "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", testId: "flujo-kpi-saldo",
      sub: `Saldo al inicio: ${data.saldo_inicial < 0 ? "−" : ""}${formatPYGCompact(Math.abs(data.saldo_inicial))}` },
    { label: "Cuentas por cobrar", value: formatPYGCompact(data.cuentas_por_cobrar), color: "text-emerald-700", bg: "bg-white", border: "border-gray-200", testId: "flujo-kpi-cxc", sub: "Pendiente de las facturas, a hoy" },
    { label: "Cuentas por pagar", value: formatPYGCompact(data.cuentas_por_pagar), color: "text-red-700", bg: "bg-white", border: "border-gray-200", testId: "flujo-kpi-cxp", sub: "Pendiente de pago, a hoy" },
  ] : [];
  const millones = (v) => `₲${(v / 1_000_000).toLocaleString("es-PY", { maximumFractionDigits: 1 })}M`;
  const input = "border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white";

  return (
    <div className="space-y-5" data-testid="flujo-real">
      <div className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm flex flex-wrap items-end gap-3">
        <div>
          <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Ver por</p>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden" data-testid="flujo-agrupar">
            {AGRUPAR_FLUJO.map(([id, label]) => (
              <button key={id} type="button" onClick={() => setAgrupar(id)}
                className={`px-3 py-1.5 text-sm font-medium ${agrupar === id ? "bg-[#2E6DA4] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                data-testid={`flujo-agrupar-${id}`}>{label}</button>
            ))}
          </div>
        </div>
        <label className="text-[11px] font-semibold text-gray-500 uppercase">Desde
          <input type="date" value={desde || data?.desde || ""} onChange={e => setDesde(e.target.value)} className={`${input} block mt-1`} data-testid="flujo-desde" />
        </label>
        <label className="text-[11px] font-semibold text-gray-500 uppercase">Hasta
          <input type="date" value={hasta || data?.hasta || ""} onChange={e => setHasta(e.target.value)} className={`${input} block mt-1`} data-testid="flujo-hasta" />
        </label>
        <div className="relative">
          <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Obras</p>
          <button type="button" onClick={() => setAbierto(v => !v)} className={`${input} min-w-[12rem] max-w-[18rem] truncate text-left`} data-testid="flujo-obras-btn">
            {textoObras} ▾
          </button>
          {abierto && (
            <div className="absolute z-30 mt-1 w-80 max-w-[85vw] bg-white border border-gray-200 rounded-xl shadow-lg p-2" data-testid="flujo-obras-panel">
              <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder="Buscar obra..." className={`${input} w-full mb-2`} data-testid="flujo-obras-buscar" />
              <div className="max-h-64 overflow-y-auto space-y-0.5">
                {listaObras.map(p => (
                  <label key={p._id} className="flex items-start gap-2 text-xs px-1.5 py-1 rounded hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={obras.includes(p._id)} onChange={() => toggleObra(p._id)} className="mt-0.5" data-testid={`flujo-obra-${p._id}`} />
                    <span>{nombreObra(p)}</span>
                  </label>
                ))}
                {!listaObras.length && <p className="text-xs text-gray-400 px-1.5 py-2">Sin obras que coincidan</p>}
              </div>
              <div className="flex justify-between gap-2 mt-2 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setObras([])} className="text-xs text-gray-600 underline" data-testid="flujo-obras-todas">Todas las obras</button>
                <button type="button" onClick={() => setAbierto(false)} className="text-xs font-semibold text-[#2E6DA4]" data-testid="flujo-obras-listo">Listo</button>
              </div>
            </div>
          )}
        </div>
        {(desde || hasta) && (
          <button type="button" onClick={() => { setDesde(""); setHasta(""); }} className="text-xs text-gray-600 underline pb-2" data-testid="flujo-rango-defecto">Rango por defecto</button>
        )}
      </div>

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="flujo-error">{error}</p>}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {loading || !data ? [0, 1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />) : kpis.map(k => (
          <div key={k.label} className={`${k.bg} ${k.border} border rounded-xl p-3`} data-testid={k.testId}>
            <p className="text-xs text-gray-500 mb-1">{k.label}</p>
            <p className={`text-lg font-bold ${k.color}`}>{k.value}</p>
            {k.sub && <p className="text-[11px] text-gray-400 mt-0.5">{k.sub}</p>}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-1 text-sm">Cobrado y pagado, con el saldo acumulado</h3>
        <p className="text-xs text-gray-400 mb-4">Arriba de cero lo cobrado y abajo lo pagado; la línea es el saldo. En millones de guaraníes (₲M).</p>
        {loading ? <Skeleton className="h-72 w-full rounded-xl" /> : !serie.length ? (
          <p className="text-sm text-gray-400 text-center py-16">Sin datos para este filtro</p>
        ) : (
          <div data-testid="flujo-grafico">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData} stackOffset="sign" margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="etiqueta" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={millones} width={70} />
                <Tooltip formatter={(v, name) => [formatPYG(Math.abs(v)) + (name === "Saldo" && v < 0 ? " (negativo)" : ""), name]}
                  contentStyle={{ borderRadius: "12px", border: "1px solid #E5E7EB", fontSize: "12px" }} />
                <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "12px" }} />
                <ReferenceLine y={0} stroke="#9CA3AF" />
                <Bar dataKey="Cobrado" stackId="flujo" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Pagado" stackId="flujo" fill="#EF4444" radius={[0, 0, 4, 4]} />
                <Line type="monotone" dataKey="Saldo" stroke="#2E6DA4" strokeWidth={2} dot={serie.length <= 40} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {!loading && serie.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-auto max-h-[420px]">
          <table className="w-full text-xs" data-testid="flujo-tabla">
            <thead className="sticky top-0 bg-gray-50 text-gray-500 uppercase text-[10px]">
              <tr><th className="px-3 py-2 text-left">Período</th><th className="px-3 py-2 text-right">Cobrado</th><th className="px-3 py-2 text-right">Pagado</th><th className="px-3 py-2 text-right">Neto</th><th className="px-3 py-2 text-right">Saldo</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {serie.map(p => (
                <tr key={p.clave} data-testid={`flujo-fila-${p.clave}`}>
                  <td className="px-3 py-1.5 text-gray-700">{p.etiqueta}</td>
                  <td className="px-3 py-1.5 text-right text-emerald-700">{formatPYG(p.cobrado)}</td>
                  <td className="px-3 py-1.5 text-right text-red-700">{formatPYG(p.pagado)}</td>
                  <td className={`px-3 py-1.5 text-right font-semibold ${p.neto < 0 ? "text-red-700" : "text-gray-800"}`}>{formatPYG(p.neto)}</td>
                  <td className={`px-3 py-1.5 text-right font-bold ${p.saldo < 0 ? "text-red-700" : "text-[#2E6DA4]"}`}>{formatPYG(p.saldo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-gray-400" data-testid="flujo-nota">Cobrado: los cobros registrados de las facturas, en la fecha de cada cobro (sin retenciones de IVA). Pagado: las Órdenes de Pago pagadas, incluidos salarios y liquidaciones, en su fecha de pago. El saldo arranca con lo cobrado menos lo pagado antes del período, según lo registrado en el sistema (no incluye saldos bancarios).</p>
    </div>
  );
}

// ============ CxC / CxP AGING TAB ============
function AgingTab({ arAging, apAging, loading, onRefresh }) {
  const [view, setView] = useState("ar");

  const handleMarkPaid = async (txnId) => {
    try {
      await axios.put(`${API}/finance/transactions/${txnId}/payment-status`, { payment_status: "pagado" }, { withCredentials: true });
      toast.success("Marcado como cobrado/pagado");
      onRefresh();
    } catch {
      toast.error("Error al actualizar estado");
    }
  };

  const data = view === "ar" ? arAging : apAging;
  const title = view === "ar" ? "Cuentas por Cobrar" : "Cuentas por Pagar";
  const allItems = AGING_CONFIG.flatMap(cfg => data?.[cfg.key]?.items || []);

  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        {[{ key: "ar", label: "CxC — Por Cobrar", icon: TrendingUp }, { key: "ap", label: "CxP — Por Pagar", icon: TrendingDown }].map(v => (
          <button key={v.key} onClick={() => setView(v.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${view === v.key ? "border-[#2E6DA4] bg-[#EBF2F9] text-[#2E6DA4]" : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"}`}
            data-testid={`aging-view-${v.key}`}>
            <v.icon size={14} />{v.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {AGING_CONFIG.map(cfg => {
            const bucket = data?.[cfg.key] || { count: 0, total: 0 };
            return (
              <div key={cfg.key} className={`${cfg.bg} ${cfg.border} border rounded-xl p-4`}>
                <p className={`text-xs font-semibold ${cfg.color} mb-1`}>{cfg.label}</p>
                <p className={`text-xl font-bold ${cfg.color}`}>{formatPYGCompact(bucket.total)}</p>
                <p className="text-xs text-gray-500 mt-1">{bucket.count} registro{bucket.count !== 1 ? "s" : ""}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">{title} — Detalle por antigüedad</h3>
            <p className="text-xs text-gray-400 mt-0.5">Total pendiente: {formatPYGCompact(data?.total || 0)}</p>
          </div>
        </div>
        {loading ? (
          <div className="p-5 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Descripción</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">N° Factura</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Monto</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Antigüedad</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Estado</th>
                  <th className="px-4 py-3 w-24 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {allItems.map(t => {
                  const cfg = AGING_CONFIG.find(c => (data?.[c.key]?.items || []).some(i => i._id === t._id)) || AGING_CONFIG[0];
                  return (
                    <tr key={t._id} className="hover:bg-gray-50/60 transition-colors" data-testid="aging-row">
                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{formatDate(t.date)}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs hidden sm:table-cell max-w-[160px] truncate">{t.description}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell font-mono">{t.invoice_number || "—"}</td>
                      <td className={`px-4 py-3 text-right font-semibold tabular-nums text-sm ${cfg.color}`}>{formatPYGCompact(t.amount_pyg)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`}>{cfg.label}</span>
                      </td>
                      <td className="px-4 py-3 text-center hidden sm:table-cell">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${t.is_overdue ? "bg-red-100 text-red-700" : PAYMENT_BADGE[t.payment_status || "pendiente"] || "bg-amber-100 text-amber-700"}`}>
                          {t.is_overdue ? "Vencido" : PAYMENT_LABEL[t.payment_status || "pendiente"]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => handleMarkPaid(t._id)} className="text-xs text-[#2E6DA4] hover:text-[#245A8A] font-semibold flex items-center gap-1 ml-auto px-2 py-1 rounded-lg hover:bg-[#EBF2F9] transition-all" data-testid="mark-paid-btn">
                          <CheckCircle size={12} /> Cobrado
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {allItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-14 text-gray-400">
                <CheckCircle size={36} className="mb-2 text-emerald-300" />
                <p className="text-sm font-medium text-gray-500">Sin {view === "ar" ? "cobros" : "pagos"} pendientes</p>
                <p className="text-xs mt-1">Todas las transacciones están al día</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ ALERTAS TAB ============
function AlertasTab({ alerts, loading, onMarkRead }) {
  const severityMap = {
    error: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", badge: "bg-red-100 text-red-700", icon: AlertTriangle },
    warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-700", icon: AlertTriangle },
    info: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-100 text-blue-700", icon: Bell },
  };

  const unread = alerts.filter(a => !a.is_read);
  const read = alerts.filter(a => a.is_read);

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : (
        <>
          {unread.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200/60 text-gray-400">
              <CheckCircle size={40} className="mb-3 text-emerald-300" />
              <p className="font-medium text-gray-500">Sin alertas activas</p>
              <p className="text-sm mt-1">El sistema está monitoreando presupuestos y liquidez</p>
            </div>
          )}

          {unread.map(alert => {
            const cfg = severityMap[alert.severity] || severityMap.info;
            const Icon = cfg.icon;
            return (
              <div key={alert._id} className={`${cfg.bg} ${cfg.border} border rounded-xl p-4 flex items-start gap-4`} data-testid="alert-item">
                <div className="p-2 rounded-lg bg-white/80 flex-shrink-0"><Icon size={16} className={cfg.text} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className={`font-semibold text-sm ${cfg.text}`}>{alert.title}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cfg.badge}`}>{alert.severity}</span>
                  </div>
                  <p className="text-sm text-gray-600">{alert.message}</p>
                  <p className="text-xs text-gray-400 mt-1.5">{formatDate(alert.created_at)}</p>
                </div>
                <button onClick={() => onMarkRead(alert._id)} className="text-xs text-gray-400 hover:text-gray-600 flex-shrink-0 px-2.5 py-1.5 rounded-lg hover:bg-white/80 transition-all font-medium border border-transparent hover:border-gray-200" data-testid="mark-read-btn">
                  Leída
                </button>
              </div>
            );
          })}

          {read.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-[0.15em] mb-3">Alertas leídas recientes</p>
              {read.slice(0, 5).map(alert => (
                <div key={alert._id} className="bg-gray-50 border border-gray-100 rounded-xl p-3 flex items-start gap-3 mb-2 opacity-60" data-testid="alert-read-item">
                  <p className="text-sm text-gray-500 flex-1">{alert.title}: {alert.message}</p>
                  <p className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{formatDate(alert.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============ MAIN PAGE ============
export default function Finance() {
  const [activeTab, setActiveTab] = useState("flujo");   // Fix IS
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(0);
  const TX_LIMIT = 100;
  const [projects, setProjects] = useState([]);
  const [arAging, setArAging] = useState(null);
  const [apAging, setApAging] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agingLoading, setAgingLoading] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const fetchTransactions = async (page = 0) => {
    const skip = page * TX_LIMIT;
    const res = await axios.get(`${API}/finance/transactions?skip=${skip}&limit=${TX_LIMIT}`, { withCredentials: true });
    setTransactions(res.data.items || []);
    setTxTotal(res.data.total || 0);
    setTxPage(page);
  };

  const fetchCore = async (page = 0) => {
    setLoading(true);
    try {
      const skip = page * TX_LIMIT;
      const [txRes, pRes] = await Promise.all([
        axios.get(`${API}/finance/transactions?skip=${skip}&limit=${TX_LIMIT}`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true }),
      ]);
      setTransactions(txRes.data.items || []);
      setTxTotal(txRes.data.total || 0);
      setTxPage(page);
      setProjects(pRes.data);
    } catch { toast.error("Error cargando datos financieros"); }
    finally { setLoading(false); }
  };

  const fetchAging = async () => {
    setAgingLoading(true);
    try {
      const [arRes, apRes] = await Promise.all([
        axios.get(`${API}/finance/ar-aging`, { withCredentials: true }),
        axios.get(`${API}/finance/ap-aging`, { withCredentials: true }),
      ]);
      setArAging(arRes.data);
      setApAging(apRes.data);
    } catch { toast.error("Error cargando CxC/CxP"); }
    finally { setAgingLoading(false); }
  };

  const fetchAlerts = async () => {
    setAlertsLoading(true);
    try {
      const { data } = await axios.get(`${API}/alerts`, { withCredentials: true });
      setAlerts(data);
    } catch {}
    finally { setAlertsLoading(false); }
  };

  useEffect(() => { fetchCore(); fetchAlerts(); }, []);
  useEffect(() => { if (activeTab === "aging" && !arAging) fetchAging(); }, [activeTab]);

  const handleMarkRead = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/read`, {}, { withCredentials: true });
      setAlerts(prev => prev.map(a => a._id === alertId ? { ...a, is_read: true } : a));
    } catch { toast.error("Error al actualizar alerta"); }
  };

  const unreadCount = alerts.filter(a => !a.is_read).length;
  const txTotalPages = Math.ceil(txTotal / TX_LIMIT);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Finanzas</h1>
          <p className="text-sm text-gray-500 mt-0.5">{txTotal} transacciones · {projects.length} proyectos activos</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm" data-testid="new-transaction-btn">
          <Plus size={16} /> Nueva Transacción
        </button>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex gap-0.5 overflow-x-auto" data-testid="finance-tabs">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${activeTab === tab.id ? "border-[#2E6DA4] text-[#2E6DA4]" : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"}`}
              data-testid={`finance-tab-${tab.id}`}>
              <tab.icon size={14} />
              {tab.label}
              {tab.id === "alertas" && unreadCount > 0 && (
                <span className="ml-0.5 text-xs bg-red-500 text-white rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-bold">{unreadCount > 9 ? "9+" : unreadCount}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      <div>
        {activeTab === "tareas" && <TareasPanel modulo="finanzas" titulo="Tareas y Flujo de Trabajo — Finanzas" />}
        {activeTab === "transacciones" && (
          <TransaccionesTab
            transactions={transactions}
            projects={projects}
            loading={loading}
            onDelete={() => fetchCore(txPage)}
            txPage={txPage}
            txTotalPages={txTotalPages}
            txTotal={txTotal}
            TX_LIMIT={TX_LIMIT}
            onPageChange={(p) => fetchTransactions(p)}
          />
        )}
        {activeTab === "flujo" && <FlujoCajaTab projects={projects} />}
        {activeTab === "aging" && <AgingTab arAging={arAging} apAging={apAging} loading={agingLoading} onRefresh={fetchAging} />}
        {activeTab === "alertas" && <AlertasTab alerts={alerts} loading={alertsLoading} onMarkRead={handleMarkRead} />}
      </div>

      <TransactionModal open={showModal} onClose={() => setShowModal(false)} onSaved={() => fetchCore(0)} projects={projects} />
    </div>
  );
}
